/**
 * Générateur — Automatisme : savoir que pour compléter une addition à trou,
 * on utilise une soustraction (2 + … = 7 se complète en calculant 7 − 2).
 * Attendu ID : 1046 | Niveau : 5ème | Domaine : Nombres relatifs
 * Rubrique : AUTOMATISME n°8 (programme 2025). Contenu source : cycle 3
 * (égalités à trous, sens du signe =) ; prépare la soustraction des relatifs,
 * la définition de l'opposé et les équations x + b = c.
 * Labels (cartes exerciseur) — deux tâches, deux cartes (revue prof 2026-09-07) :
 *   - Série flash     (4 trous saisie : le nombre manquant — « je calcule »)
 *   - Quel calcul ?   (« je nomme l'opération », quatre formats : QCM sur
 *                     l'égalité nue, Drag « c'est c − g » en toutes lettres,
 *                     Vrai/Faux à 3 affirmations, Problème = la citerne en QCM)
 *
 * ── Plan de cartes validé (2026-09-07, cadrage prof) ───────────────────────
 * Le cœur n'est PAS de trouver le nombre manquant (acquis au cycle 3) mais de
 * RECONNAÎTRE et NOMMER l'opération : « je calcule c − g ». Deux régimes :
 *
 *   RÉGIME « JE CALCULE » (Série flash, et le format Drag de Quel calcul ?) : le
 *   calcul doit être immédiat, l'effort porte sur le geste. Trois cas, un
 *   tiers chacun :
 *     1. petits nombres (l'exemple du programme) : terme donné à UN chiffre,
 *        arrivée ≤ 30, emprunt autorisé (2 + … = 7 ; 8 + … = 23) ;
 *     2. écart sans emprunt, entiers à deux chiffres, chiffre à chiffre sans
 *        emprunt (42 + … = 67) — assert dédié ;
 *     3. rond, exempté de l'assert par construction : arrivée multiple de 10
 *        ou 100 (départ quelconque en dessous : 48 + … = 60, 37 + … = 100,
 *        120 + … = 300 — multiple de 10 au-delà de 100) ou de 1 000 (départ
 *        multiple de 10) ; décimaux UNIQUEMENT ici, en complément à l'entier
 *        ou au demi-entier (0,8 + … = 3 ; 7,5 + … = 10 ; 1,25 + … = 2 ;
 *        2,75 + … = 3,5), comme la série flash de la fiche.
 *   Contrôle sur les huit lignes de la fiche : 2 + … = 7 et … + 8 = 23 → cas
 *   1, les six autres → cas 3. Aucune n'est rejetée.
 *
 *   RÉGIME « JE CHOISIS L'OPÉRATION » (Quel calcul ? : formats QCM, Vrai/Faux
 *   et Problème) : aucun résultat demandé, on charge pour rendre le comptage
 *   impossible : entiers jusqu'à 999 avec retenues libres, décimaux à deux
 *   décimales quelconques (4,85 + … = 12,3).
 *
 * Variables communes : position du trou (premier ou second terme, à parts
 * égales) ; terme donné plus grand ou plus petit que le trou (alterné).
 * Distracteurs des QCM et du V/F : g − c (le sens de la soustraction
 * inversé), et la somme écrite tantôt g + c tantôt c + g (confusion avec
 * l'opération de l'égalité) — sinon le troisième choix serait repérable.
 * La citerne : formulation tirée parmi trois pour un même problème ; le mot
 * « ajouter » à côté d'une soustraction attendue est le piège voulu, les
 * deux autres (« combien manque-t-il », « combien reste-t-il à… ») sont
 * neutres.
 *
 * EXCLUSIONS garanties par assert : trou nul, trou égal au terme donné
 * (5 + … = 10 laisse deviner), négatifs, lettre, soustraction à trou, trou
 * sur la somme, trois décimales. Tout en CENTIÈMES ENTIERS, zéro flottant,
 * chaque égalité re-vérifiée (throw sinon).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, filtrerDistracteurs, tex,
} from '../../../utils';

const ATTENDU_ID = 1046;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_CALCUL = 'Quel calcul ?';

// ─── Le domaine (centièmes entiers) ───────────────────────────────────────

/** n centièmes → écriture canonique française (« 2,75 », « 4,1 », « 4 »). */
const fmt = (n100) => {
    const ent = Math.floor(n100 / 100);
    const c = String(n100 % 100).padStart(2, '0').replace(/0+$/, '');
    return c === '' ? String(ent) : `${ent},${c}`;
};

/** Écriture française → centièmes (re-parse de vérification). */
const parse100 = (s) => {
    const m = String(s).match(/^(\d+)(?:,(\d{1,2}))?$/);
    if (!m) return null;
    return parseInt(m[1], 10) * 100 + parseInt(((m[2] || '') + '00').slice(0, 2), 10);
};

const enTex = (s) => String(s).replace(',', '{,}');

/**
 * Une égalité à trou : g + t = c (position 'second' : « g + … = c ») ou
 * t + g = c (position 'premier' : « … + g = c »). g = terme donné, t = trou,
 * c = arrivée. Tout en centièmes.
 */
const verifierEgalite = (g100, c100, position, cas) => {
    const t100 = c100 - g100;
    if (!Number.isInteger(g100) || !Number.isInteger(c100)
        || g100 <= 0 || t100 <= 0 || t100 === g100
        || c100 > 100000 // 1 000 au plus (le complément à mille)
        || parse100(fmt(g100)) !== g100 || parse100(fmt(c100)) !== c100 || parse100(fmt(t100)) !== t100) {
        throw new Error(`gen_1046 : égalité invalide ${g100} + ${t100} = ${c100}`);
    }
    if (!['premier', 'second'].includes(position)) throw new Error('gen_1046 : position inconnue');
    return { g100, t100, c100, position, cas };
};

/** Cas 2 : chiffre à chiffre, aucune colonne de g + t ne dépasse 9. */
const sansEmprunt = (g, t) => {
    let a = g;
    let b = t;
    while (a > 0 || b > 0) {
        if ((a % 10) + (b % 10) > 9) return false;
        a = Math.floor(a / 10);
        b = Math.floor(b / 10);
    }
    return true;
};

const positionAleatoire = () => randomChoice(['premier', 'second']);

// ─── Régime « je calcule » ────────────────────────────────────────────────

const tirerPetit = () => {
    for (let essai = 0; essai < 60; essai++) {
        const g = randInt(2, 9);
        // Trou d'au moins 2 : « 2 + … = 3 » se lit sans geste (revue 2026-09-07).
        const c = randInt(g + 2, 30);
        if (c === 2 * g) continue;
        return verifierEgalite(g * 100, c * 100, positionAleatoire(), 'petit');
    }
    return verifierEgalite(800, 2300, 'premier', 'petit');
};

const tirerEcart = () => {
    for (let essai = 0; essai < 60; essai++) {
        const g = randInt(10, 88);
        const t = randInt(10, 89);
        const c = g + t;
        if (c > 99 || t === g || !sansEmprunt(g, t) || c % 10 === 0) continue;
        return verifierEgalite(g * 100, c * 100, positionAleatoire(), 'ecart');
    }
    return verifierEgalite(4200, 6700, 'second', 'ecart');
};

const tirerRond = () => {
    for (let essai = 0; essai < 60; essai++) {
        const sousCas = randomChoice(['dizaine', 'cent', 'mille', 'decimal_une', 'decimal_deux']);
        let g100 = 0;
        let c100 = 0;
        if (sousCas === 'dizaine') {
            // 48 + … = 60 ; 37 + … = 100 : arrivée 20…100, départ quelconque
            // en dessous, jamais multiple de 10 (sinon c'est le cas « cent »).
            const c = randInt(2, 10) * 10;
            // Trou d'au moins 2 (même raison que le cas petits nombres).
            const g = randInt(Math.max(1, c - 39), c - 2);
            if (g % 10 === 0) continue;
            g100 = g * 100; c100 = c * 100;
        } else if (sousCas === 'cent') {
            // 120 + … = 300 : arrivée 200…900, départ multiple de 10.
            const c = randInt(2, 9) * 100;
            const g = randInt(1, c / 10 - 1) * 10;
            if (g % 100 === 0) continue;
            g100 = g * 100; c100 = c * 100;
        } else if (sousCas === 'mille') {
            // 250 + … = 1 000 : départ multiple de 10.
            const g = randInt(1, 99) * 10;
            if (g % 100 === 0) continue;
            g100 = g * 100; c100 = 100000;
        } else if (sousCas === 'decimal_une') {
            // 0,8 + … = 3 ; 7,5 + … = 10 ; 2,7 + … = 5 : départ à une
            // décimale sous 10 ; arrivée = le demi-entier ou l'entier juste
            // au-dessus, ou 5, ou 10 (les compléments de la fiche). Pas
            // d'arrivée quelconque : 1,2 + … = 8,5 n'est plus un complément.
            g100 = randInt(1, 99) * 10;
            if (g100 % 100 === 0) continue;
            const suivant = Math.ceil(g100 / 50) * 50;
            c100 = randomChoice([suivant, suivant === g100 ? suivant + 50 : suivant, 500, 1000].filter((c) => c > g100));
        } else {
            // 1,25 + … = 2 ; 2,75 + … = 3,5 : deux décimales, arrivée = le
            // demi-entier ou l'entier juste au-dessus.
            g100 = randInt(1, 999);
            if (g100 % 10 === 0) continue;
            c100 = Math.ceil(g100 / 50) * 50;
        }
        const t100 = c100 - g100;
        if (t100 <= 0 || t100 === g100 || c100 > 100000) continue;
        return verifierEgalite(g100, c100, positionAleatoire(), 'rond');
    }
    return verifierEgalite(4800, 6000, 'second', 'rond');
};

const TIRAGES_CALCUL = { petit: tirerPetit, ecart: tirerEcart, rond: tirerRond };
const tirerCalcul = (cas) => TIRAGES_CALCUL[cas || randomChoice(['petit', 'ecart', 'rond'])]();

// ─── Régime « je choisis l'opération » ────────────────────────────────────

const tirerOperation = (entiersSeulement = false) => {
    for (let essai = 0; essai < 60; essai++) {
        if (entiersSeulement || randomChoice([true, false])) {
            // Arrivée entre 100 et 999 : on charge (le comptage est impossible)
            // et l'exemple du coup de pouce, 14 + … = 32, ne peut pas sortir
            // (revue 2026-09-07, auto-testé dans cdp1046.self.test.js).
            const g = randInt(10, 989);
            const t = randInt(10, 989);
            if (g + t > 999 || g + t < 100 || t === g) continue;
            return verifierEgalite(g * 100, (g + t) * 100, positionAleatoire(), 'operation');
        }
        const g100 = randInt(50, 5000);
        const t100 = randInt(50, 5000);
        const c100 = g100 + t100;
        if (c100 > 9999 || t100 === g100) continue;
        // Au moins un des deux nombres affichés porte une virgule.
        if (g100 % 100 === 0 && c100 % 100 === 0) continue;
        return verifierEgalite(g100, c100, positionAleatoire(), 'operation');
    }
    return verifierEgalite(13700, 28400, 'second', 'operation');
};

// ─── Écritures ────────────────────────────────────────────────────────────

/** « 48 + … = 60 » ou « … + 8 = 23 », en texte brut. */
const egaliteTexte = (e) => (e.position === 'second'
    ? `${fmt(e.g100)} + … = ${fmt(e.c100)}`
    : `… + ${fmt(e.g100)} = ${fmt(e.c100)}`);

/** La même égalité en KaTeX (énoncé). */
const egaliteTex = (e) => tex(e.position === 'second'
    ? `${enTex(fmt(e.g100))} + \\ldots = ${enTex(fmt(e.c100))}`
    : `\\ldots + ${enTex(fmt(e.g100))} = ${enTex(fmt(e.c100))}`);

const MOINS = '−';
const calculTexte = (x100, y100, op) => `${fmt(x100)} ${op === '-' ? MOINS : '+'} ${fmt(y100)}`;

/**
 * Les trois calculs proposés : le bon (c − g), le sens inversé (g − c) et la
 * somme, écrite g + c ou c + g au hasard. Distincts par construction (g ≠ c,
 * et une somme n'est jamais une différence des mêmes nombres).
 */
const choixCalculs = (e) => {
    const sommeInversee = randomChoice([true, false]);
    return {
        bon: calculTexte(e.c100, e.g100, '-'),
        faux: [
            { texte: calculTexte(e.g100, e.c100, '-'), code: 'sens_inverse' },
            { texte: sommeInversee ? calculTexte(e.c100, e.g100, '+') : calculTexte(e.g100, e.c100, '+'), code: 'addition' },
        ],
    };
};

const EXPLICATIONS = {
    sens_inverse: 'On part de l\'arrivée et on retire ce qu\'on a déjà : le grand nombre moins le petit.',
    addition: 'L\'égalité contient un +, mais pour retrouver ce qui manque on fait l\'opération inverse : une soustraction.',
    regle: 'Trou dans une addition : on soustrait. Le nombre qui manque, c\'est l\'arrivée moins le terme donné.',
};

// ─── Carte 1 : Série flash (trous saisie) ─────────────────────────────────

/** Une ligne KaTeX entière avec le trou à sa place : « $2 + {{5}} = 7$ ». */
const ligneFlash = (e) => {
    const trou = '{{' + fmt(e.t100) + '}}';
    const corps = e.position === 'second'
        ? `${enTex(fmt(e.g100))} + ${trou} = ${enTex(fmt(e.c100))}`
        : `${trou} + ${enTex(fmt(e.g100))} = ${enTex(fmt(e.c100))}`;
    return '$' + corps + '$';
};

const genFlash = () => {
    for (let essai = 0; essai < 60; essai++) {
        // Quatre lignes (cinq, c'est trop d'un coup : revue prof 2026-09-07) :
        // les trois cas une fois chacun, puis un quatrième tiré.
        const cas = shuffle(['petit', 'ecart', 'rond', randomChoice(['petit', 'ecart', 'rond'])]);
        const items = cas.map(tirerCalcul);
        // Terme donné plus grand ou plus petit que le trou : les deux présents.
        const plusGrand = items.filter((e) => e.g100 > e.t100).length;
        if (plusGrand === 0 || plusGrand === items.length) continue;
        const positions = new Set(items.map((e) => e.position));
        if (positions.size < 2) continue;
        // Quatre lignes vraiment différentes : arrivées, termes donnés et
        // trous tous distincts (sinon « 6 + … = 9 » et « … + 3 = 9 » se suivent).
        const arrivees = new Set(items.map((e) => e.c100));
        const donnes = new Set(items.map((e) => e.g100));
        const trous = new Set(items.map((e) => e.t100));
        if (arrivees.size < 4 || donnes.size < 4 || trous.size < 4) continue;
        const phrase = items.map(ligneFlash).join('\n');
        const payload = makeTexteTrou(
            phrase,
            '<p><b>Complète chaque égalité.</b></p>',
            { ...opts(L_FLASH), mode: 'saisie' },
        );
        payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
        payload._meta.params = { items };
        return payload;
    }
    const items = [
        verifierEgalite(200, 700, 'second', 'petit'),
        verifierEgalite(4200, 6700, 'second', 'ecart'),
        verifierEgalite(350, 1000, 'second', 'rond'),
        verifierEgalite(270, 500, 'premier', 'rond'),
    ];
    const phrase = items.map(ligneFlash).join('\n');
    const payload = makeTexteTrou(phrase, '<p><b>Complète chaque égalité.</b></p>', { ...opts(L_FLASH), mode: 'saisie' });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { items };
    return payload;
};

// ─── Carte 2, format A : QCM sur l'égalité nue (sans résultat) ────────────

const genCalculQcm = () => {
    const e = tirerOperation();
    const { bon, faux } = choixCalculs(e);
    const payload = makeQcm(
        [bon, ...faux.map((x) => x.texte)],
        bon,
        `<p><b>Pour compléter ${egaliteTex(e)}, quel calcul fais-tu ?</b></p>`,
        opts(L_CALCUL),
        false,
    );
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { ...e, distracteurs: faux.map(({ code }) => ({ code })) };
    return payload;
};

// ─── Carte 2, format B : le calcul en toutes lettres (trous drag) ─────────

const genLettresDrag = () => {
    const e = tirerCalcul();
    const g = fmt(e.g100);
    const c = fmt(e.c100);
    // Position 'second' : « le nombre qu'il faut ajouter à g pour obtenir c » ;
    // position 'premier' : « le nombre auquel on ajoute g pour obtenir c ».
    const debut = e.position === 'second'
        ? `Le nombre qu'il faut ajouter à ${g} pour obtenir ${c}`
        : `Le nombre auquel on ajoute ${g} pour obtenir ${c}`;
    const phrase = `${egaliteTexte(e)}\n${debut}, c'est {{${c}}} ${MOINS} {{${g}}}.`;
    // Pièges : la somme g + c (il additionne) et le trou lui-même (il pose la
    // réponse à la place du calcul). filtrerDistracteurs écarte toute
    // collision avec c ou g.
    const distracteurs = filtrerDistracteurs(phrase, [fmt(e.g100 + e.c100), fmt(e.t100)]);
    const payload = makeTexteTrou(
        phrase,
        '<p><b>Complète le calcul avec les bonnes étiquettes.</b></p>',
        { ...opts(L_CALCUL), mode: 'drag', distracteurs },
    );
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { ...e, distracteurs };
    return payload;
};

// ─── Carte 2, format C : vrai ou faux (3 affirmations, 1 ou 2 vraies) ─────

const genVf = () => {
    for (let essai = 0; essai < 60; essai++) {
        const items = [tirerOperation(), tirerOperation(), tirerOperation()];
        if (new Set(items.map(egaliteTexte)).size < 3) continue;
        // 1 ou 2 vraies, jamais un schéma fixe, jamais tout-vrai ni tout-faux.
        const nbVraies = randomChoice([1, 2]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(3 - nbVraies).fill(false)]);
        const codes = [];
        const lignes = items.map((e, i) => {
            const { bon, faux } = choixCalculs(e);
            let calcul = bon;
            if (!verites[i]) {
                const f = randomChoice(faux);
                calcul = f.texte;
                codes.push(f.code);
            }
            return `Pour compléter ${egaliteTexte(e)}, on calcule ${calcul}`;
        });
        const payload = makeVraiFaux(
            lignes,
            verites.map((v) => (v ? 0 : 1)),
            '<p><b>Vrai ou faux ?</b></p>',
            opts(L_CALCUL),
        );
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { items, verites, distracteurs: codes.map((code) => ({ code })) };
        return payload;
    }
    const items = [
        verifierEgalite(13700, 28400, 'second', 'operation'),
        verifierEgalite(48500, 123000, 'premier', 'operation'),
        verifierEgalite(6200, 90100, 'second', 'operation'),
    ];
    const lignes = [
        `Pour compléter ${egaliteTexte(items[0])}, on calcule ${calculTexte(28400, 13700, '-')}`,
        `Pour compléter ${egaliteTexte(items[1])}, on calcule ${calculTexte(48500, 123000, '-')}`,
        `Pour compléter ${egaliteTexte(items[2])}, on calcule ${calculTexte(90100, 6200, '-')}`,
    ];
    const payload = makeVraiFaux(lignes, [0, 1, 0], '<p><b>Vrai ou faux ?</b></p>', opts(L_CALCUL));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { items, verites: [true, false, true], distracteurs: [{ code: 'sens_inverse' }] };
    return payload;
};

// ─── Carte 2, format D : la citerne (QCM mini-problème) ───────────────────

/** Prénom et pronom sujet : les questions s'accordent (« doit-elle »). */
const PRENOMS = [
    { nom: 'Léa', p: 'elle' }, { nom: 'Sami', p: 'il' }, { nom: 'Inès', p: 'elle' },
    { nom: 'Noah', p: 'il' }, { nom: 'Maya', p: 'elle' }, { nom: 'Yanis', p: 'il' },
];

/**
 * Trois habillages. `entiers` : les timbres ne se coupent pas. Les trois
 * questions d'un habillage disent la même chose : la première contient
 * « ajouter » (le piège lexical, une soustraction derrière un mot
 * d'addition), les deux autres sont neutres.
 */
const HABILLAGES = [
    {
        id: 'citerne', entiers: false,
        situation: (g, c) => `Une citerne contient ${g} L d'eau. On veut qu'elle en contienne ${c} L.`,
        questions: [
            () => 'Quelle quantité d\'eau faut-il ajouter ?',
            () => 'Combien de litres manque-t-il ?',
            () => 'Combien de litres reste-t-il à verser ?',
        ],
    },
    {
        id: 'argent', entiers: false,
        situation: (g, c, pr) => `${pr.nom} a ${g} €. Il lui faut ${c} € pour son cadeau.`,
        questions: [
            (pr) => `Quelle somme doit-${pr.p} ajouter à ses économies ?`,
            () => 'Combien lui manque-t-il ?',
            () => 'Combien lui reste-t-il à économiser ?',
        ],
    },
    {
        id: 'timbres', entiers: true,
        situation: (g, c, pr) => `${pr.nom} a ${g} timbres. La collection complète en compte ${c}.`,
        questions: [
            (pr) => `Combien de timbres doit-${pr.p} ajouter à sa collection ?`,
            () => 'Combien de timbres lui manque-t-il ?',
            () => 'Combien de timbres lui reste-t-il à trouver ?',
        ],
    },
];

const genCiterneQcm = () => {
    const hab = randomChoice(HABILLAGES);
    const e = tirerOperation(hab.entiers);
    const prenom = randomChoice(PRENOMS);
    const formulation = randInt(0, hab.questions.length - 1);
    const question = hab.questions[formulation](prenom);
    const { bon, faux } = choixCalculs(e);
    const payload = makeQcm(
        [bon, ...faux.map((x) => x.texte)],
        bon,
        `<p>${hab.situation(fmt(e.g100), fmt(e.c100), prenom)}</p><p><b>${question} Quel calcul donne la réponse ?</b></p>`,
        opts(L_CALCUL),
        false,
    );
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = {
        ...e, habillage: hab.id, formulation, distracteurs: faux.map(({ code }) => ({ code })),
    };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

/**
 * Deux MISCONCEPTIONS nommées, toujours présentes (aucune rotation : trois
 * choix seulement, surcharge cognitive à quatre — arbitrage prof
 * 2026-09-07). L'élève ne calcule rien : c'est le sens de la soustraction
 * qui est testé, pas le résultat.
 */
export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'trois choix : c − g (bon), g − c, et la somme écrite g + c OU c + g au hasard (sinon le troisième choix se repère à sa forme)',
        'régime « je choisis l\'opération » : entiers jusqu\'à 999 avec retenues libres, décimaux à deux décimales quelconques — le comptage est impossible, seule la reconnaissance de l\'opération répond',
        'chaque distracteur porte son code dans _meta.params.distracteurs',
    ],
    calcul_qcm_unique: {
        support: 'une égalité à trou, trou premier ou second terme',
        fixes: {
            sens_inverse: 'g − c : il soustrait dans l\'ordre de lecture (le petit moins le grand)',
            addition: 'g + c ou c + g : il reproduit le signe de l\'égalité',
        },
    },
    citerne_qcm_unique: {
        support: 'un mini-problème parties-tout (citerne, argent, timbres), formulation « ajouter » (piège lexical) ou neutre',
        fixes: {
            sens_inverse: 'le contenu moins l\'objectif',
            addition: 'le contenu plus l\'objectif : le mot « ajouter » ou le fait de compléter appelle une addition',
        },
    },
    vf_tableau_vf: {
        support: 'trois égalités distinctes, 1 ou 2 affirmations vraies',
        fixes: 'mêmes codes que le QCM, un par ligne fausse',
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    calcul_qcm_unique: genCalculQcm,
    lettres_texte_trou_drag: genLettresDrag,
    vf_tableau_vf: genVf,
    citerne_qcm_unique: genCiterneQcm,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'calcul', label: L_CALCUL, formatsList: [
        { key: 'calcul_qcm_unique', label: 'QCM' },
        { key: 'lettres_texte_trou_drag', label: 'Drag' },
        { key: 'vf_tableau_vf', label: 'Vrai/Faux' },
        { key: 'citerne_qcm_unique', label: 'Problème' },
    ] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
